import { computed, signal } from "@preact/signals";
import { empty, nonempty } from "@uri/gamla";
import { useEffect, useRef, useState } from "preact/hooks";
import { ConversationWithParticipants, Group } from "./db.ts";
import "./style.css";
import {
  formatLastActivity,
  Loading,
  NumberInput,
  ObfuscatedText,
  obfuscateIdsSignal,
  SelectInput,
  TextInput,
  useIsMobile,
} from "./uiComponents.tsx";
import { participantColor, primaryColor, t } from "./theme.ts";
import { updateUrlParams } from "./urlParams.ts";
import { convosError, retryConversationsSignal } from "./convoPage.tsx";
import { AlertCircle, Funnel, RefreshCw } from "lucide-preact";
import { useLocation } from "preact-iso";
import {
  normalizeSinceInput,
  normalizeTimestamp,
  parseSinceDate,
} from "../../src/timeUtils.ts";

export const allGroups = "All groups";

const sortedGroupOptions = () => {
  const sorted = [...groups.value].sort(
    (a, b) => (b.lastActivityAt ?? 0) - (a.lastActivityAt ?? 0),
  );
  return [
    { id: "", name: allGroups },
    ...sorted.map((g) => ({
      id: g.id,
      name: `${g.name} (${formatLastActivity(g.lastActivityAt)})`,
    })),
  ];
};

const ConvosErrorState = ({ th }: { th: ReturnType<typeof t> }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      flex: 1,
      padding: 32,
      gap: 12,
      textAlign: "center",
    }}
  >
    <AlertCircle size={32} color={th.danger || "#ef4444"} />
    <span
      style={{
        fontSize: 18,
        fontWeight: 600,
        color: th.text,
        textAlign: "center",
      }}
    >
      Unable to load conversations
    </span>
    <span
      style={{
        fontSize: 14,
        color: th.textMuted,
        maxWidth: 320,
        textAlign: "center",
      }}
    >
      A network or timeout error occurred while fetching conversations.
    </span>
    <button
      type="button"
      className="compact-button"
      style={{
        fontSize: 13,
        padding: "6px 16px",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontWeight: 600,
        cursor: "pointer",
        marginTop: 4,
      }}
      onClick={() => retryConversationsSignal.value?.()}
    >
      <RefreshCw size={14} />
      Retry
    </button>
  </div>
);

const getThStyle = (): any => {
  const th = t();
  return {
    padding: "8px 12px",
    textAlign: "left",
    color: th.textDim,
    fontSize: 12,
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    background: th.surfaceBg,
    position: "sticky",
    top: 0,
  };
};

const ConversationTableHeader = ({ isMobile }: { isMobile: boolean }) => {
  const thStyle = getThStyle();
  return (
    <thead>
      <tr>
        <th style={{ ...thStyle, width: isMobile ? "55%" : "65%" }}>Users</th>
        <th style={{ ...thStyle, width: isMobile ? "45%" : "35%" }}>Last</th>
      </tr>
    </thead>
  );
};

const getTdStyle = (): any => ({
  padding: "10px 12px",
  borderBottom: `1px solid ${t().border}`,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const getTimeStr = (time: number | undefined) => {
  const norm = normalizeTimestamp(time);
  return norm > 0 ? new Date(norm).toLocaleString() : "-";
};

// Shared context menu state — only one menu open at a time
const contextMenu = signal<{ x: number; y: number; name: string } | null>(null);

// Close the context menu on any outside click/touch
if (typeof window !== "undefined") {
  const dismissMenu = () => {
    contextMenu.value = null;
  };
  document.addEventListener("mousedown", dismissMenu);
  document.addEventListener("touchstart", dismissMenu);
  document.addEventListener("scroll", dismissMenu, true);
}

const ParticipantContextMenu = () => {
  const menu = contextMenu.value;
  if (!menu) return null;
  const th = t();
  return (
    <div
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        left: menu.x,
        top: menu.y,
        zIndex: 9999,
        background: th.cardBg,
        border: `1px solid ${th.inputBorder}`,
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
        padding: "4px 0",
        minWidth: 160,
      }}
    >
      <div
        onClick={() => {
          participantSearchSignal.value = menu.name;
          contextMenu.value = null;
        }}
        style={{
          padding: "8px 14px",
          fontSize: 13,
          color: th.text,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background = th.hoverBg;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = "transparent";
        }}
      >
        Filter by this participant
      </div>
    </div>
  );
};

const ParticipantChip = (
  { from, allParticipants }: { from: string; allParticipants: string[] },
) => {
  const longPressTimer = useRef<number | null>(null);
  const didLongPress = useRef(false);

  const openMenu = (x: number, y: number) => {
    // Clamp so the menu doesn't overflow the viewport
    const clampedX = Math.min(x, globalThis.innerWidth - 180);
    const clampedY = Math.min(y, globalThis.innerHeight - 60);
    contextMenu.value = { x: clampedX, y: clampedY, name: from };
  };

  const handleContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openMenu(e.clientX, e.clientY);
  };

  const handleTouchStart = (e: TouchEvent) => {
    didLongPress.current = false;
    const touch = e.touches[0];
    const tx = touch.clientX;
    const ty = touch.clientY;
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      openMenu(tx, ty);
    }, 500) as unknown as number;
  };

  const handleTouchEnd = (e: Event) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (didLongPress.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleTouchMove = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  return (
    <span
      title={from}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      style={{
        background: participantColor(from, allParticipants).bg,
        color: participantColor(from, allParticipants).text,
        borderRadius: 12,
        padding: "2px 8px",
        fontSize: 13,
        fontWeight: 500,
        display: "inline-block",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        flexShrink: 0,
        cursor: "context-menu",
        WebkitTouchCallout: "none",
        userSelect: "none",
      }}
    >
      <ObfuscatedText text={from} />
    </span>
  );
};

const getParticipants = (participants: string[] | undefined) => {
  const unique = (participants ?? []).filter(Boolean).sort();
  if (empty(unique)) {
    return <span style={{ color: t().textDim }}>—</span>;
  }
  return (
    <span
      style={{
        display: "flex",
        gap: 4,
        maxWidth: "100%",
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      {unique.map((from) =>
        from
          ? <ParticipantChip key={from} from={from} allParticipants={unique} />
          : null
      )}
    </span>
  );
};

const ConversationRow = (
  { conversation, onSelect, index, selected }: {
    conversation: ConversationWithParticipants;
    onSelect: () => void;
    index: number;
    selected?: boolean;
  },
) => {
  const tdStyle = getTdStyle();
  const th = t();
  return (
    <tr
      id={`convo-row-${conversation.id}`}
      style={{
        cursor: "pointer",
        background: selected
          ? th.primaryBg
          : index % 2 === 0
          ? "transparent"
          : "rgba(255,255,255,0.02)",
        borderLeft: selected
          ? `3px solid ${th.primary}`
          : "3px solid transparent",
      }}
      onClick={onSelect}
    >
      <td style={tdStyle}>
        {getParticipants(conversation.participants)}
      </td>
      <td style={{ ...tdStyle, overflow: "visible" }}>
        {getTimeStr(conversation.lastMessageTime)}
      </td>
    </tr>
  );
};

export { normalizeSinceInput, parseSinceDate };

export const sinceDate = computed<Date | null>(
  () => parseSinceDate(since.value),
);

const getUrlParam = (key: string) => {
  if (typeof location === "undefined") return null;
  const params = new URLSearchParams(location.search);
  const val = params.get(key);
  if (!val) return null;
  const decoded = decodeURIComponent(val);
  if (key === "groupId" || key === "convo") {
    // Strip accidental trailing punctuation from copy-pastes
    return decoded.replace(/[^a-zA-Z0-9-]/g, "");
  }
  return decoded;
};

export const groups = signal<Group[]>([]);
export const selectedGroup = signal<string | null>(getUrlParam("groupId"));
export const loadingConversations = signal(false);
export const selectedConversation = signal<string | null>(null);
export const convos = signal<ConversationWithParticipants[]>([]);

const defaultTime = "last 24 hours";

const since = signal<string>(getUrlParam("since") || "");

export const textSearchSignal = signal<string>(getUrlParam("text") || "");

export const participantSearchSignal = signal<string>(
  getUrlParam("participant") || "",
);

export const conversationNameSignal = signal<string>(
  getUrlParam("conversationName") || "",
);

// Sync all filter signals from current URL params.
// Called from ConversationsView useEffect on navigation.
export const syncFiltersFromUrl = () => {
  const params = new URLSearchParams(globalThis.location.search);

  // groupId
  const groupIdRaw = params.get("groupId");
  if (!groupIdRaw) {
    if (selectedGroup.value) selectedGroup.value = null;
  } else {
    const groupId = decodeURIComponent(groupIdRaw).replace(
      /[^a-zA-Z0-9-]/g,
      "",
    );
    if (groups.value.length) {
      const found = groups.value.some((g) => g.id === groupId);
      if (found) {
        if (selectedGroup.value !== groupId) selectedGroup.value = groupId;
      } else {
        if (selectedGroup.value) selectedGroup.value = null;
      }
    }
    // If groups not loaded yet, module-level effect in convoPage handles it
  }

  // text filters
  const text = params.get("text") ?? "";
  if (textSearchSignal.value !== text) textSearchSignal.value = text;

  const participant = params.get("participant") ?? "";
  if (participantSearchSignal.value !== participant) {
    participantSearchSignal.value = participant;
  }

  const conversationName = params.get("conversationName") ?? "";
  if (conversationNameSignal.value !== conversationName) {
    conversationNameSignal.value = conversationName;
  }

  const sinceParam = params.get("since") ?? "";
  if (since.value !== sinceParam) since.value = sinceParam;
};

export const visibleConvosLimit = signal(30);

export const hasMoreConvos = signal(false);
export const loadNextConvos = signal<() => void>(() => {
  visibleConvosLimit.value += 30;
});

export const showMobileFilters = signal(false);

const ConversationTable = () => {
  const th = t();
  return (
    <div
      style={{ flex: 1, width: "100%", overflow: "auto" }}
      onScroll={(e) => {
        const target = e.target as HTMLDivElement;
        const isNearBottom =
          target.scrollHeight - target.scrollTop - target.clientHeight < 100;
        if (isNearBottom && hasMoreConvos.value) {
          loadNextConvos.value();
        }
      }}
    >
      <ParticipantContextMenu />
      {convosError.value && (
        <div
          style={{
            padding: "6px 12px",
            backgroundColor: "rgba(239, 68, 68, 0.12)",
            borderBottom: `1px solid ${th.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: 12,
            color: th.text,
          }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Could not refresh ({convosError.value.error.message})
          </span>
          <button
            type="button"
            onClick={() => retryConversationsSignal.value?.()}
            style={{
              background: "transparent",
              border: "none",
              color: primaryColor(),
              fontWeight: 600,
              cursor: "pointer",
              padding: "2px 6px",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              flexShrink: 0,
            }}
          >
            <RefreshCw size={11} />
            Retry
          </button>
        </div>
      )}
      <table style={{ width: "100%", tableLayout: "fixed" }}>
        <ConversationTableHeader isMobile={false} />
        <tbody>
          {convos.value.map((c, i) => (
            <ConversationRow
              key={c.id}
              conversation={c}
              index={i}
              selected={selectedConversation.value === c.id}
              onSelect={() => {
                selectedConversation.value = c.id;
              }}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const handleUpdateUrlParams = (route?: (path: string) => void) => {
  const params = new URLSearchParams(globalThis.location.search);
  if (selectedGroup.value) {
    params.set("groupId", selectedGroup.value);
  } else {
    params.delete("groupId");
  }
  if (since.value && since.value.trim() !== "") {
    params.set("since", since.value);
  } else {
    params.delete("since");
  }
  if (textSearchSignal.value) {
    params.set("text", textSearchSignal.value);
  } else {
    params.delete("text");
  }
  if (participantSearchSignal.value) {
    params.set("participant", participantSearchSignal.value);
  } else {
    params.delete("participant");
  }
  if (conversationNameSignal.value) {
    params.set("conversationName", conversationNameSignal.value);
  } else {
    params.delete("conversationName");
  }
  if (selectedConversation.value) {
    params.set("convo", selectedConversation.value);
  } else {
    params.delete("convo");
    params.delete("conversationId");
  }
  updateUrlParams(params, route);
};

// Desktop horizontal filter bar
export const FilterBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const th = t();

  const filterInputStyle = {
    background: th.inputBg,
    color: th.text,
    border: `1px solid ${th.inputBorder}`,
    borderRadius: 6,
    padding: "4px 8px",
    fontSize: 13,
  };

  const groupOptions = sortedGroupOptions();
  const filteredOptions = groupOptions.filter((o) =>
    o.name.toLowerCase().includes(filter.toLowerCase())
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [filter]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedName =
    groupOptions.find((o) => o.id === (selectedGroup.value ?? ""))?.name ??
      allGroups;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
        padding: "10px 12px",
        borderBottom: `1px solid ${th.border}`,
        alignItems: "center",
        backgroundColor: th.surfaceBg,
      }}
    >
      {/* Group filter with type-to-filter */}
      <div ref={dropdownRef} style={{ position: "relative" }}>
        <div
          onClick={() => setIsOpen(!isOpen)}
          style={{
            ...filterInputStyle,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
            minWidth: 120,
            maxWidth: 260,
          }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {selectedName}
          </span>
          <span style={{ fontSize: 10 }}>{isOpen ? "▲" : "▼"}</span>
        </div>
        {isOpen && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              zIndex: 1000,
              background: th.cardBg,
              border: `1px solid ${th.inputBorder}`,
              borderRadius: 6,
              marginTop: 4,
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              minWidth: 200,
              maxWidth: 300,
            }}
          >
            <input
              autoFocus
              style={{
                margin: 8,
                width: "calc(100% - 16px)",
                background: th.inputBg,
                color: th.text,
                border: `1px solid ${th.inputBorder}`,
                borderRadius: 6,
                padding: "6px 10px",
                fontSize: 13,
                boxSizing: "border-box",
              }}
              placeholder="Type to filter..."
              value={filter}
              onInput={(e) => setFilter(e.currentTarget.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setHighlightedIndex((prev) =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                  );
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  if (filteredOptions[highlightedIndex]) {
                    const opt = filteredOptions[highlightedIndex];
                    selectedGroup.value = opt.id || null;
                    setIsOpen(false);
                    setFilter("");
                  }
                } else if (e.key === "Escape") {
                  setIsOpen(false);
                }
              }}
            />
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              {filteredOptions.length > 0
                ? (
                  filteredOptions.map((opt, index) => (
                    <div
                      key={opt.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectedGroup.value = opt.id || null;
                        setIsOpen(false);
                        setFilter("");
                      }}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        background: index === highlightedIndex
                          ? th.hoverBg
                          : "transparent",
                        color: index === highlightedIndex
                          ? th.primary
                          : th.text,
                        fontSize: 13,
                      }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      {opt.name}
                    </div>
                  ))
                )
                : (
                  <div
                    style={{
                      padding: "8px 12px",
                      color: th.textDim,
                      fontSize: 13,
                    }}
                  >
                    No matches
                  </div>
                )}
            </div>
          </div>
        )}
      </div>

      {/* Text filter */}
      <input
        type="text"
        placeholder="Search text..."
        value={textSearchSignal.value}
        onInput={(e) =>
          textSearchSignal.value = (e.target as HTMLInputElement).value}
        style={{
          ...filterInputStyle,
          padding: "4px 10px",
          width: 120,
        }}
      />

      {/* Participant filter */}
      <input
        type="text"
        placeholder="Participant..."
        value={participantSearchSignal.value}
        onInput={(e) =>
          participantSearchSignal.value = (e.target as HTMLInputElement).value}
        style={{
          ...filterInputStyle,
          padding: "4px 10px",
          width: 100,
        }}
      />

      {/* Conversation name filter */}
      <input
        type="text"
        placeholder="Conversation..."
        value={conversationNameSignal.value}
        onInput={(e) =>
          conversationNameSignal.value = (e.target as HTMLInputElement).value}
        style={{
          ...filterInputStyle,
          padding: "4px 10px",
          width: 130,
        }}
      />

      {/* Since filter */}
      <input
        type="text"
        placeholder={defaultTime}
        value={since.value}
        onInput={(e) => since.value = (e.target as HTMLInputElement).value}
        style={{
          ...filterInputStyle,
          padding: "4px 10px",
          width: 110,
        }}
      />
    </div>
  );
};

// Mobile-only filter inputs component
export const MobileFilters = () => {
  const initialMount = useRef(true);
  const { route } = useLocation();
  const th = t();

  // Sync signal changes back to URL — but skip the initial mount since the URL
  // already has the correct params from the deep link.
  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }
    selectedConversation.value = null;
    handleUpdateUrlParams(route);
  }, [
    selectedGroup.value,
    since.value,
    textSearchSignal.value,
    participantSearchSignal.value,
    conversationNameSignal.value,
  ]);

  return (
    <div
      style={{
        backgroundColor: th.surfaceBg,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: 16,
      }}
    >
      <div style={{ flex: 1, overflowY: "auto" }}>
        <SelectInput
          name="Group"
          value={selectedGroup.value ?? ""}
          onChange={(v) => {
            selectedGroup.value = v;
          }}
          options={sortedGroupOptions()}
        />
        <TextInput name="Since" signal={since} placeholder={defaultTime} />
        <span
          style={{
            fontSize: 12,
            color: th.textMuted,
            marginBottom: 6,
            marginLeft: 2,
          }}
        >
          {(() => {
            const raw = since.value?.trim();
            if (!raw) return "";
            const parsed = parseSinceDate(raw);
            if (parsed) return parsed.toString();
            return 'Could not parse date (e.g. "last 24 hours", "yesterday")';
          })()}
        </span>
        <TextInput name="Text" signal={textSearchSignal} />
        <TextInput name="Participant" signal={participantSearchSignal} />
        <TextInput
          name="Conversation name"
          signal={conversationNameSignal}
        />
      </div>
      {/* Sticky bottom section with results count and button */}
      <div
        style={{
          padding: "16px 0",
          background: th.surfaceBg,
          borderTop: `1px solid ${th.border}`,
          marginTop: 16,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: 24, color: th.textDim, fontWeight: 500 }}>
          {convos.value.length}{" "}
          {convos.value.length === 1 ? "result" : "results"}
        </span>
        <button
          type="button"
          onClick={() => {
            // Navigate back to chat list by clearing view param
            const newParams = new URLSearchParams(globalThis.location.search);
            newParams.delete("view");
            updateUrlParams(newParams, route);
          }}
          style={{
            width: "100%",
            background: th.hoverBg,
            color: th.text,
            border: `1px solid ${th.inputBorder}`,
            borderRadius: 8,
            padding: "12px 20px",
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          See results
        </button>
      </div>
    </div>
  );
};

// Mobile-only chat list component
export const MobileChatList = () => {
  const isMobile = true;
  const { route } = useLocation();
  const th = t();

  return (
    <div
      style={{
        backgroundColor: th.surfaceBg,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <ParticipantContextMenu />
      {/* Chat list */}
      {loadingConversations.value && empty(convos.value) && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            minHeight: 180,
          }}
        >
          <Loading text="Loading conversations" />
        </div>
      )}
      {(!loadingConversations.value || nonempty(convos.value)) && (
        <div style={{ flex: 1, overflow: "hidden" }}>
          {convos.value.length === 0
            ? convosError.value ? <ConvosErrorState th={th} /> : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  padding: 32,
                }}
              >
                <span
                  style={{
                    fontSize: 24,
                    fontWeight: 600,
                    color: th.primary,
                    textAlign: "center",
                  }}
                >
                  No matching conversations
                </span>
              </div>
            )
            : (
              <div
                style={{ height: "100%", overflow: "auto" }}
                onScroll={(e) => {
                  const target = e.target as HTMLDivElement;
                  const isNearBottom = target.scrollHeight - target.scrollTop -
                      target.clientHeight < 100;
                  if (isNearBottom && hasMoreConvos.value) {
                    loadNextConvos.value();
                  }
                }}
              >
                {convosError.value && (
                  <div
                    style={{
                      padding: "6px 12px",
                      backgroundColor: "rgba(239, 68, 68, 0.12)",
                      borderBottom: `1px solid ${th.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: 12,
                      color: th.text,
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Could not refresh ({convosError.value.error.message})
                    </span>
                    <button
                      type="button"
                      onClick={() => retryConversationsSignal.value?.()}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: primaryColor(),
                        fontWeight: 600,
                        cursor: "pointer",
                        padding: "2px 6px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        flexShrink: 0,
                      }}
                    >
                      <RefreshCw size={11} />
                      Retry
                    </button>
                  </div>
                )}
                <table style={{ width: "100%", tableLayout: "fixed" }}>
                  <ConversationTableHeader isMobile={isMobile} />
                  <tbody>
                    {convos.value.map((c, i) => (
                      <ConversationRow
                        key={c.id}
                        conversation={c}
                        index={i}
                        selected={selectedConversation.value === c.id}
                        onSelect={() => {
                          // Navigate to conversation view by updating URL
                          const newParams = new URLSearchParams(
                            globalThis.location.search,
                          );
                          newParams.set("conversationId", c.id);
                          newParams.delete("view");
                          updateUrlParams(newParams, route);
                        }}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}
      {/* Floating filter button */}
      <button
        type="button"
        onClick={() => {
          // Navigate to filters view by setting view param
          const newParams = new URLSearchParams(globalThis.location.search);
          newParams.set("view", "filter");
          newParams.delete("conversationId");
          updateUrlParams(newParams, route);
        }}
        style={{
          position: "absolute",
          bottom: 20,
          right: 20,
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: th.hoverBg,
          border: "none",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10,
        }}
      >
        <Funnel size={24} color={th.text} />
      </button>
    </div>
  );
};

export const Filters = () => {
  const initialMount = useRef(true);
  const isMobile = useIsMobile();
  const { route } = useLocation();
  const th = t();

  // Sync signal changes back to URL — but skip the initial mount since the URL
  // already has the correct params from the deep link.
  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }
    selectedConversation.value = null;
    handleUpdateUrlParams(route);
  }, [
    selectedGroup.value,
    since.value,
    textSearchSignal.value,
    participantSearchSignal.value,
    conversationNameSignal.value,
  ]);

  return (
    <div
      style={{
        backgroundColor: th.surfaceBg,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Desktop: Conversation list only, filters moved to top */}
      {!isMobile && (
        <>
          {loadingConversations.value && empty(convos.value) && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
                minHeight: 180,
              }}
            >
              <Loading text="Loading conversations" />
            </div>
          )}
          {(!loadingConversations.value || nonempty(convos.value)) && (
            convos.value.length === 0
              ? convosError.value ? <ConvosErrorState th={th} /> : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                    padding: 32,
                  }}
                >
                  <span
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: th.textDim,
                      textAlign: "center",
                    }}
                  >
                    No matching conversations
                  </span>
                </div>
              )
              : <ConversationTable />
          )}
        </>
      )}

      {/* Mobile: Vertical filter stack */}
      {isMobile && (
        <div style={{ display: "flex", flexDirection: "column", padding: 16 }}>
          <SelectInput
            name="Group"
            value={selectedGroup.value ?? ""}
            onChange={(v) => {
              selectedGroup.value = v;
            }}
            options={sortedGroupOptions()}
          />
          <TextInput name="Since" signal={since} placeholder={defaultTime} />
          <span
            style={{
              fontSize: 12,
              color: th.textMuted,
              marginBottom: 6,
              marginLeft: 2,
            }}
          >
            {(() => {
              const raw = since.value?.trim();
              if (!raw) {
                return "";
              }
              const parsed = parseSinceDate(raw);
              if (parsed) {
                return parsed.toString();
              }
              return 'Could not parse date (e.g. "last 24 hours", "yesterday")';
            })()}
          </span>
          <TextInput name="Text" signal={textSearchSignal} />
          <TextInput name="Participant" signal={participantSearchSignal} />
          <TextInput
            name="Conversation name"
            signal={conversationNameSignal}
          />
          {
            /*
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#aab0be",
              fontSize: 14,
              cursor: "pointer",
              marginTop: 8,
            }}
          >
            <input
              type="checkbox"
              checked={obfuscateIdsSignal.value}
              onChange={(e) => {
                obfuscateIdsSignal.value = e.currentTarget.checked;
              }}
            />
            Obfuscate User IDs
          </label>
          */
          }
        </div>
      )}

      {/* Mobile: Loading, error, and conversation list */}
      {isMobile && (
        <>
          {loadingConversations.value && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
                minHeight: 180,
              }}
            >
              <Loading text="Loading conversations" />
            </div>
          )}
          {!loadingConversations.value && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
                minHeight: 180,
                cursor: convos.value.length === 0 ? "default" : "pointer",
                userSelect: "none",
              }}
              onClick={() => {
                if (convos.value.length === 0) return;
                if (!selectedConversation.value) {
                  const conversationId = new URLSearchParams(
                    globalThis.location.search,
                  ).get("conversationId");
                  if (conversationId) {
                    selectedConversation.value = conversationId;
                  }
                }
                if (
                  !convos.value.some((c) => c.id === selectedConversation.value)
                ) {
                  selectedConversation.value = convos.value[0].id;
                }
              }}
              title={convos.value.length === 0
                ? undefined
                : "Close filters and return to conversations"}
            >
              {convos.value.length === 0
                ? convosError.value ? <ConvosErrorState th={th} /> : (
                  <span
                    style={{
                      fontSize: 32,
                      fontWeight: 600,
                      color: th.primary,
                      letterSpacing: 1,
                      textAlign: "center",
                      textShadow: "0 2px 8px #0006",
                    }}
                  >
                    No matching conversations
                  </span>
                )
                : <ConversationTable />}
            </div>
          )}
        </>
      )}
    </div>
  );
};
